import { useEffect, useRef, useState } from 'react';
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';
import { loadGooglePlaces } from '../lib/googlePlaces';
import { cn } from '../lib/utils';
import type { StructuredAddress } from '../types';

/**
 * Props for the AddressAutocomplete component.
 */
interface AddressAutocompleteProps {
  onSelect: (address: StructuredAddress) => void;
  defaultValue?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Extracts a specific address component from Google geocode results.
 *
 * Args:
 *   components (google.maps.GeocoderAddressComponent[]): Address components.
 *   type (string): Component type to find (e.g., 'locality').
 *   useShortName (boolean): Whether to use short_name instead of long_name.
 *
 * Returns:
 *   string: The component value, or empty string if not found.
 */
function extractComponent(
  components: google.maps.GeocoderAddressComponent[],
  type: string,
  useShortName = false
): string {
  const component = components.find((c) => c.types.includes(type));
  if (!component) return '';
  return useShortName ? component.short_name : component.long_name;
}

/**
 * Parses Google geocode result into a StructuredAddress.
 *
 * Args:
 *   result (google.maps.GeocoderResult): Geocode result from Google.
 *
 * Returns:
 *   StructuredAddress: Parsed address with street, city, state, zip, formatted.
 */
function parseGeocoderResult(
  result: google.maps.GeocoderResult
): StructuredAddress {
  const components = result.address_components;
  const streetNumber = extractComponent(components, 'street_number');
  const route = extractComponent(components, 'route');
  const city =
    extractComponent(components, 'locality') ||
    extractComponent(components, 'sublocality');
  const state = extractComponent(
    components,
    'administrative_area_level_1',
    true
  );
  const zip = extractComponent(components, 'postal_code');

  const street = [streetNumber, route].filter(Boolean).join(' ');

  return {
    street,
    city,
    state,
    zip,
    formatted: result.formatted_address,
  };
}

/**
 * Reusable address input with Google Places autocomplete.
 * Degrades gracefully to a plain text input when no API key is configured.
 *
 * Args:
 *   onSelect (function): Called with StructuredAddress when user selects a suggestion.
 *   defaultValue (string): Pre-fill value for the input.
 *   placeholder (string): Input placeholder text.
 *   disabled (boolean): Whether the input is disabled.
 *   className (string): Additional CSS class names.
 */
export default function AddressAutocomplete({
  onSelect,
  defaultValue = '',
  placeholder = 'Start typing an address...',
  disabled = false,
  className = '',
}: AddressAutocompleteProps) {
  const [placesReady, setPlacesReady] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLUListElement>(null);

  // Reason: Load Google Places API on mount; if it fails or no key, we fall back
  // to a plain text input (graceful degradation).
  useEffect(() => {
    loadGooglePlaces()
      .then(() => {
        // Reason: Check if the API actually loaded (key could be invalid)
        if (window.google?.maps?.places) {
          setPlacesReady(true);
        }
      })
      .catch((err) => {
        console.warn('[AddressAutocomplete] Failed to load Google Places:', err);
      });
  }, []);

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
    init,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'us' },
      types: ['address'],
    },
    debounce: 300,
    defaultValue,
    initOnMount: false,
  });

  // Reason: Initialize the hook only after Places API is confirmed loaded.
  useEffect(() => {
    if (placesReady) {
      init();
    }
  }, [placesReady, init]);

  const hasSuggestions = status === 'OK' && data.length > 0;

  /**
   * Handles selection of an autocomplete suggestion.
   * Geocodes the selected address and calls onSelect with structured result.
   *
   * Args:
   *   description (string): The formatted address string from the suggestion.
   */
  async function handleSelect(description: string) {
    setValue(description, false);
    clearSuggestions();
    setSelectedIndex(-1);

    try {
      const results = await getGeocode({ address: description });
      if (results.length > 0) {
        const structured = parseGeocoderResult(results[0]);
        onSelect(structured);
      }
    } catch (err) {
      console.error('[AddressAutocomplete] Geocode failed:', err);
    }
  }

  /**
   * Handles keyboard navigation within the suggestion dropdown.
   *
   * Args:
   *   e (React.KeyboardEvent): The keyboard event.
   */
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!hasSuggestions) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, data.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handleSelect(data[selectedIndex].description);
    } else if (e.key === 'Escape') {
      clearSuggestions();
      setSelectedIndex(-1);
    }
  }

  // Reason: cn() (tailwind-merge) lets a caller's className override the
  // default light-theme utilities below — e.g. V2 dark-token inputs — instead
  // of both sets fighting by source order.
  const inputClasses = cn(
    'w-full border rounded-md px-3 py-2 text-sm',
    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    className,
  );

  // Reason: If Places API is not loaded, render a plain text input so the
  // component is always usable (just without autocomplete).
  if (!placesReady) {
    return (
      <input
        type="text"
        defaultValue={defaultValue}
        placeholder={placeholder}
        disabled={disabled}
        className={inputClasses}
        onChange={(e) => {
          // Reason: Without Places API, provide the raw text as formatted address
          onSelect({
            street: e.target.value,
            city: '',
            state: '',
            zip: '',
            formatted: e.target.value,
          });
        }}
      />
    );
  }

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Reason: Small delay allows click on suggestion to register before clearing
          setTimeout(() => clearSuggestions(), 150);
        }}
        disabled={disabled || !ready}
        placeholder={placeholder}
        className={inputClasses}
        role="combobox"
        aria-expanded={hasSuggestions}
        aria-autocomplete="list"
        aria-controls="address-suggestions"
      />

      {hasSuggestions && (
        <ul
          ref={dropdownRef}
          id="address-suggestions"
          role="listbox"
          className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {data.map((suggestion, index) => (
            <li
              key={suggestion.place_id}
              role="option"
              aria-selected={index === selectedIndex}
              className={`px-3 py-2 text-sm cursor-pointer ${
                index === selectedIndex
                  ? 'bg-blue-50 text-blue-900'
                  : 'hover:bg-slate-100'
              }`}
              onMouseDown={() => handleSelect(suggestion.description)}
            >
              {suggestion.description}
            </li>
          ))}
          <li className="px-3 py-1.5 text-xs text-gray-400 border-t">
            Powered by Google
          </li>
        </ul>
      )}
    </div>
  );
}
